import React, { useState } from "react";
import UpdateModal from "./UpdateModal"; // Ensure this path is correct
import { updateUserProfile, updateUserPassword } from "../../services/user.service";
import { normalizeAvatarSrc, isAvatarImgSrc } from "../../utils/profileImage";

export default function AuthorizedInfo({ initialData }) {
  const [authorizedInfo, setAuthorizedInfo] = useState(initialData || { description: "", profile: { image: "", name: "", email: "" }, fields: [] });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editField, setEditField] = useState(null);

  const handleOpenModal = (field) => {
    setEditField(field);
    setIsModalOpen(true);
  };

  const handleSave = async (updatedValue) => {
    try {
      if (editField === "Phone Number") {
        // Prefer unified profile update; backend accepts phone
        await updateUserProfile({ phone: updatedValue });
      } else if (editField === "Name Surname") {
        await updateUserProfile({ name: updatedValue });
      } else if (editField === "Job Title") {
        await updateUserProfile({ job_title: updatedValue });
      } else if (editField === "Avatar URL") {
        await updateUserProfile({ avatar_url: updatedValue });
      } else if (editField === "Password") {
        await updateUserPassword(updatedValue?.current_password, updatedValue?.new_password);
      }
      setAuthorizedInfo((prev) => {
        const updatedFields = (prev.fields || []).map((f) =>
          f.label === editField ? { ...f, value: updatedValue, action: "Update" } : f
        );

        // Update profile name/email if needed
        let updatedProfile = { ...prev.profile };
        if (editField === "Name Surname") {
          updatedProfile.name = updatedValue;
        } else if (editField === "E-Mail Address") {
          updatedProfile.email = updatedValue;
        } else if (editField === "Avatar URL") {
          updatedProfile.image = normalizeAvatarSrc(updatedValue || "");
        }

        return { ...prev, profile: updatedProfile, fields: updatedFields };
      });
    } catch (e) {
      const backendMsg = e?.response?.data?.message;
      const reqId = e?.response?.data?.request_id || e?.response?.headers?.["x-request-id"];
      const msg = backendMsg ? `${backendMsg}${reqId ? ` (request_id: ${reqId})` : ""}` : (e.message || "Failed to update information");
      alert(msg);
    } finally {
      setIsModalOpen(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Left Description */}
        <div>
          <h3 className="font-medium text-gray-900 text-base mb-2">
            Authorized information
          </h3>
          <p className="text-xs text-gray-500 leading-relaxed">
            {authorizedInfo.description}
          </p>
        </div>

        {/* Right Content */}
        <div className="border rounded-lg p-4 w-full">
          {/* Profile Section */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full overflow-hidden bg-emerald-500/80 text-white flex items-center justify-center text-sm font-semibold cursor-pointer"
              title="Click to update avatar"
              onClick={() => { setEditField("Avatar URL"); setIsModalOpen(true); }}
            >
              {isAvatarImgSrc(authorizedInfo.profile.image) ? (
                <img
                  src={normalizeAvatarSrc(authorizedInfo.profile.image)}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                (authorizedInfo.profile.name || "?").split(" ").map(p => p[0]).join("").slice(0,2)
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">{authorizedInfo.profile.name}</p>
              <p className="text-sm text-gray-500">{authorizedInfo.profile.email}</p>
            </div>
          </div>

          <hr className="mb-2" />

          {/* Fields */}
         <div className="divide-y">
  {(authorizedInfo.fields || []).map((field, index) => (
    <div key={index} className="py-4">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm text-gray-600">{field.label}</p>

          {/* Custom Rendering */}
          {field.label === "Password" ? (
            <p className="text-sm font-medium text-gray-900">********</p>
          ) : field.label === "Phone Number" ? (
            <div>
              {Array.isArray(field.value) ? (
                field.value.map((num, idx) => (
                  <p key={idx} className="text-sm font-medium text-gray-900">
                    {num}
                  </p>
                ))
              ) : (
                <p className="text-sm font-medium text-gray-900">{field.value}</p>
              )}
            </div>
          ) : field.label === "Name Surname" ? (
            <p className="text-sm font-medium text-gray-900">
              {Array.isArray(field.value)
                ? field.value.join(" ") // Combines [first, last]
                : field.value}
            </p>
          ) : (
            <p className="text-sm font-medium text-gray-900">{field.value}</p>
          )}
        </div>

        <button
          type="button"
          className="text-teal-600 text-sm font-medium"
          onClick={() => handleOpenModal(field.label)}
        >
          {field.label === "Phone Number"
            ? ((Array.isArray(field.value) && field.value.length === 0) || !field.value || field.value === "None"
                ? "Add"
                : "Update")
            : field.action}
        </button>
      </div>
    </div>
  ))}
</div>

        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <UpdateModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          field={editField}
          currentValue={
            editField === "Avatar URL"
              ? (authorizedInfo.profile.image || "")
              : (authorizedInfo.fields.find((f) => f.label === editField)?.value || "")
          }
        />
      )}
    </div>
  );
}
